import type { GraphEdge, GraphNode } from '@/features/backtest/schemas/backtest-types'
import { restoreSavedGraph } from '@/features/backtest/headless-graph-service'

/**
 * What each AI edit to a saved strategy replaced, keyed by its tool_use_id
 * (2026-10-15, one-assistant Phase 2c), so the Undo banner writes that graph
 * back. Same pattern as the AI drawing batches: the earlier graph is only known
 * once the call has run, after the snapshot was taken. Plain module state,
 * bounded.
 */
export interface SavedStrategyEdit {
  strategyId: string
  previous: { nodes: GraphNode[]; edges: GraphEdge[] }
  /** The revision the edit produced: the precondition for writing back. */
  revision: number
}

const MAX_EDITS = 20
const edits = new Map<string, SavedStrategyEdit>()

export function recordSavedStrategyEdit(toolUseId: string, edit: SavedStrategyEdit): void {
  edits.set(toolUseId, edit)
  while (edits.size > MAX_EDITS) edits.delete(edits.keys().next().value as string)
}

/**
 * Write one edit's earlier graph back; false when nothing is recorded for it.
 * A failure (an edit made since, the network) is logged; the strategy keeps
 * whatever the server holds.
 */
export function revertSavedStrategyEdit(toolUseId: string): boolean {
  const edit = edits.get(toolUseId)
  if (!edit) return false
  edits.delete(toolUseId)
  void restoreSavedGraph({
    strategyId: edit.strategyId,
    nodes: edit.previous.nodes,
    edges: edit.previous.edges,
    expectedGraphRevision: edit.revision,
  }).catch((error: unknown) => {
    console.warn('[companion] could not undo the saved strategy edit', error)
  })
  return true
}

/** Test seam. */
export function resetSavedStrategyEdits(): void {
  edits.clear()
}
